import { createContext, useContext, useEffect, useId, useMemo, useState } from 'react';
import { isReadRefusal } from '../hooks/useScreenData';

// #583 S6a, design §7.5 "common states": one small set every screen can use, so loading, empty,
// error and stale can never be mistaken for each other.

// #583 S6b1 (S6a review L7): a live region inserted together with its text is announced only by
// some screen readers, so loading, empty and stale are spoken through ONE region the shell renders
// from the start, and each state swaps in a short sentence. Errors keep role="alert": an alert is
// announced when it is inserted, and saying it in the shell too would say it twice.
const Announcer = createContext(null);

export function LiveRegion({ children }) {
  const [entries, setEntries] = useState(() => new Map());
  const announcer = useMemo(() => ({
    say: (owner, text) => setEntries((now) => new Map(now).set(owner, text)),
    unsay: (owner) => setEntries((now) => {
      const next = new Map(now);
      next.delete(owner);
      return next;
    }),
  }), []);
  // Insertion order is deterministic. Deduplicate speech, not ownership: equal sentences
  // remain until their last caller leaves. Only short state labels enter this region. An entry
  // that already ends a sentence is not given a second period (S6b2 fix round 1, L6).
  const text = [...new Set(entries.values())].map((t, i, all) => (i < all.length - 1 && !/[.!?]$/.test(t) ? `${t}.` : t)).join(' ');
  return (
    <Announcer.Provider value={announcer}>
      {children}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true" data-live-region="polite">{text}</div>
    </Announcer.Provider>
  );
}

/** Speak `text` through the shell's region while the calling state is on screen. */
export function useAnnounce(text) {
  const announcer = useContext(Announcer);
  const owner = useId();
  useEffect(() => {
    if (!announcer || !text) return undefined;
    announcer.say(owner, text);
    return () => announcer.unsay(owner);
  }, [announcer, owner, text]);
}

// #583 S6b2: how long an event (an action's outcome) stays in the region. The region is atomic, so
// a sentence left there would be read again by the next state that joins it (the 60 s stale one).
export const ANNOUNCE_HOLD_MS = 10_000;

/** Speak `text` once for each new `stamp`, then take it out of the region; the page keeps showing it. */
export function useAnnounceOnce(text, stamp) {
  const announcer = useContext(Announcer);
  const owner = useId();
  useEffect(() => {
    if (!announcer || !text) return undefined;
    announcer.say(owner, text);
    const timer = setTimeout(() => announcer.unsay(owner), ANNOUNCE_HOLD_MS);
    return () => { clearTimeout(timer); announcer.unsay(owner); };
  }, [announcer, owner, text, stamp]);
}

/**
 * A skeleton the size of the content it stands in for. Announced once, not line by line. A role-less
 * div is `generic`, whose name is not exposed, so the skeleton is a named group (S6b1 review M2).
 */
export function LoadingState({ label, lines = 3 }) {
  useAnnounce(label);
  return (
    <div className="page-state page-loading" role="group" aria-busy="true" aria-label={label}>
      {Array.from({ length: lines }, (_, i) => <div key={i} className="skeleton-line" aria-hidden="true" />)}
    </div>
  );
}

/** A successful answer with nothing in it. Never used for a failure. */
export function EmptyState({ title, children }) {
  useAnnounce(title);
  return (
    <div className="page-state page-empty">
      <p className="page-state-title">{title}</p>
      {children && <p>{children}</p>}
    </div>
  );
}

// Move keyboard focus before a transient action removes itself. Pointer actions that did not
// focus the button leave focus alone. The heading remains mounted throughout a list read.
export function focusPageBeforeAction(event) {
  if (document.activeElement !== event.currentTarget) return;
  const heading = event.currentTarget.closest('main')?.querySelector('h2');
  if (heading) {
    heading.tabIndex = -1;
    heading.focus();
  }
}

export function ReadError({ error, onRetry, title }) {
  return <InlineError title={error.status === 400 && !title ? 'Check the search' : title} message={error.message}
    onAction={isReadRefusal(error) ? undefined : onRetry} />;
}

/** A failed read, inline, with the one action that can fix it (Retry by default). */
export function InlineError({ title = 'Could not load', message, actionLabel = 'Retry', onAction }) {
  return (
    <div className="page-state page-error" role="alert">
      <p className="page-state-title">{title}</p>
      {message && <p>{message}</p>}
      {onAction && <button type="button" className="btn btn-outline" onClick={(event) => { focusPageBeforeAction(event); onAction(); }}>{actionLabel}</button>}
    </div>
  );
}

const STALE_REASONS = { failed: 'refresh failed', aged: 'more than a minute old' };

/**
 * Prior data kept after a failed refresh, or data 60 s old (§7.5). The screen disables its actions
 * while this shows; `onReload` re-reads the same query and page.
 */
export function StaleNotice({ loadedAt, reason = 'failed', onReload }) {
  const clock = `${loadedAt.toISOString().slice(11, 19)} UTC`;
  useAnnounce(`Last loaded ${clock}; ${STALE_REASONS[reason]}`);
  return (
    <div className="stale-notice">
      <p>
        Last loaded <time dateTime={loadedAt.toISOString()}>{clock}</time>; {STALE_REASONS[reason]}
      </p>
      {onReload && <button type="button" className="btn btn-outline" onClick={(event) => { focusPageBeforeAction(event); onReload(); }}>Reload</button>}
    </div>
  );
}
